import { Routes, Route, Navigate, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { LayoutList, LogOut, Building2 } from "lucide-react";
import ChangePasswordDialog from "../../components/ChangePasswordDialog";
import QueueTable from "./QueueTable";
import ParticipantReview from "./ParticipantReview";

/**
 * Participant Management Console — the external FinBPM partner's authenticated
 * home (participant-console-002). This is the ONLY ticket in the pack that owns
 * console routing: the shell mounts a nested `<Routes>` subtree so every later
 * frontend ticket (004 review pane, 005 KYC viewer, etc.) adds a nested route
 * here and NEVER touches `App.js`.
 *
 * Trust boundary (pack DECISION, Kevin 2026-06-14): the partner is external
 * staff — this shell deliberately does NOT reuse the internal `DashboardLayout`
 * (which renders the staff `Sidebar`). It carries its own partner-scoped frame,
 * header, and nav so no internal navigation surface ever leaks to the partner.
 */

const ConsoleFrame = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Partner-scoped header — distinct from the internal dashboard chrome. */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo.png" alt="EchoSense Capital Club" className="w-9 h-9 object-contain shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.3em] uppercase text-burgundy">Partner Console</p>
              <h1 className="text-base font-serif font-semibold text-slate-900 truncate">
                Participant Review
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
              <Building2 size={15} className="text-slate-400" />
              <span className="truncate max-w-[180px]" data-testid="console-partner-identity">
                {user?.full_name || user?.email || "Partner"}
              </span>
            </div>
            {/* Authenticated self-service password change (forge-20260615-001).
                Stays inside the partner surface — no admin/member nav introduced. */}
            <ChangePasswordDialog
              triggerVariant="outline"
              triggerSize="sm"
              triggerClassName="border-slate-300 text-slate-700"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleLogout}
              className="border-slate-300 text-slate-700"
              data-testid="console-logout"
            >
              <LogOut size={14} className="mr-1.5" />
              Sign out
            </Button>
          </div>
        </div>

        {/* Console nav — the seam later tickets extend with additional tabs. */}
        <nav className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1 -mb-px">
            <NavLink
              to="/console"
              end
              data-testid="console-nav-queue"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition ${
                  isActive
                    ? "border-burgundy text-burgundy"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`
              }
            >
              <LayoutList size={15} />
              Work Queue
            </NavLink>
          </div>
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6" data-testid="console-content">
        {children}
      </main>
    </div>
  );
};

const ParticipantConsole = () => {
  return (
    <ConsoleFrame>
      <Routes>
        <Route index element={<QueueTable />} />
        {/* Consolidated review pane (participant-console-004) — the queue's
            row → `/console/:userId` navigation resolves here. */}
        <Route path=":userId" element={<ParticipantReview />} />
        {/* Unknown nested path → back to the queue. */}
        <Route path="*" element={<Navigate to="/console" replace />} />
      </Routes>
    </ConsoleFrame>
  );
};

export default ParticipantConsole;
